import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { resolveType } from '@/services/ServiceContainer';
import { UserService } from '@/services/UserService';

interface CodeOfConductModalProps {
    leagueId: string;
    leagueName: string;
    codeOfConduct: string;
    userId: string;
    onClose: () => void;
    onDecline: () => void;
    onAccept: () => void;
}

const CodeOfConductModal: React.FC<CodeOfConductModalProps> = ({
    leagueId,
    leagueName,
    codeOfConduct,
    onClose,
    onDecline,
    onAccept
}) => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const [content, setContent] = useState<string>('');

    useEffect(() => {
        const fetchContent = async () => {
            if (codeOfConduct.startsWith('/')) {
                try {
                    const response = await fetch(codeOfConduct);
                    const text = await response.text();
                    // Extract content between <body> tags
                    const bodyContent = text.match(/<body[^>]*>([\s\S]*)<\/body>/i)?.[1] || '';
                    setContent(bodyContent);
                } catch (error) {
                    console.error('Error fetching code of conduct:', error);
                    setContent('Error loading code of conduct. Please try again later.');
                }
            } else {
                setContent(codeOfConduct);
            }
        };

        fetchContent();
    }, [codeOfConduct]);

    const handleAccept = async () => {
        if (!user?.uid) {
            console.error('No authenticated user found');
            return;
        }

        try {
            const userService = resolveType(UserService);
            const userModel = await userService.get(user.uid);
            if (!userModel) {
                throw new Error('User document not found');
            }

            const updatedActiveLeagues = {
                ...userModel.activeLeagues,
                [leagueId]: {
                    ...userModel.activeLeagues[leagueId],
                    codeOfConductAcceptedDate: Timestamp.now()
                }
            };

            // Update the entire activeLeagues object
            await userService.update(user.uid, updatedActiveLeagues);

            console.log('Update completed successfully');
            onAccept();
        } catch (error) {
            console.error('Error accepting code of conduct:', error);
        }
    };

    const handleDecline = () => {
        console.log('Decline button clicked');
        onDecline();
    };

    const modalStyle = {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    };

    const modalContentStyle = {
        backgroundColor: theme.colors.BACKGROUND,
        padding: theme.spacing.LARGE,
        borderRadius: theme.borderRadius.MEDIUM,
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column' as const,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    };

    const headerStyle = {
        color: theme.colors.TEXT,
        marginBottom: theme.spacing.MEDIUM,
        fontSize: theme.fonts.LARGE,
        fontWeight: 'bold',
    };

    const contentStyle = {
        color: theme.colors.TEXT,
        marginBottom: theme.spacing.LARGE,
        lineHeight: '1.6',
        overflowY: 'auto' as const,
        flex: 1,
        padding: theme.spacing.LARGE,
        backgroundColor: theme.colors.SURFACE,
        borderRadius: theme.borderRadius.SMALL,
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: theme.colors.TEXT,
            marginTop: theme.spacing.XLARGE,
            marginBottom: theme.spacing.MEDIUM,
            lineHeight: '1.3',
        },
        '& p': {
            marginBottom: theme.spacing.MEDIUM,
            fontSize: '1.1em',
        },
        '& ul, & ol': {
            marginLeft: theme.spacing.XLARGE,
            marginBottom: theme.spacing.LARGE,
            marginTop: theme.spacing.MEDIUM,
        },
        '& li': {
            marginBottom: theme.spacing.MEDIUM,
            fontSize: '1.1em',
            lineHeight: '1.6',
        },
        '& strong': {
            fontWeight: 'bold',
        },
        '& em': {
            fontStyle: 'italic',
        },
        '& ul li ul, & ol li ol': {
            marginTop: theme.spacing.SMALL,
            marginBottom: theme.spacing.SMALL,
        },
        '& ul li ul li, & ol li ol li': {
            marginBottom: theme.spacing.SMALL,
        }
    };

    const buttonContainerStyle = {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing.MEDIUM,
        marginTop: theme.spacing.MEDIUM,
    };

    const buttonStyle = {
        padding: `${theme.spacing.SMALL}px ${theme.spacing.MEDIUM}px`,
        borderRadius: theme.borderRadius.SMALL,
        border: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
    };

    const acceptButtonStyle = {
        ...buttonStyle,
        backgroundColor: theme.colors.PRIMARY,
        color: theme.colors.BACKGROUND,
    };

    const declineButtonStyle = {
        ...buttonStyle,
        backgroundColor: theme.colors.SURFACE,
        color: theme.colors.TEXT,
        border: `1px solid ${theme.colors.BORDER}`,
    };

    return (
        <div style={modalStyle} onClick={onClose}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <h2 style={headerStyle}>Code of Conduct - {leagueName}</h2>
                <div
                    style={contentStyle}
                    dangerouslySetInnerHTML={{ __html: content }}
                />
                <div style={buttonContainerStyle}>
                    <button style={declineButtonStyle} onClick={handleDecline}>
                        Decline
                    </button>
                    <button
                        style={acceptButtonStyle}
                        onClick={handleAccept}
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CodeOfConductModal; 